import * as React from "react";
import {ICustomerPortalService, Instalment, PartyProperty, PartyPropertyEnum, Types} from "@falcon/services";
import * as moment from "moment";
import {fromEvent, Subscription} from "rxjs";
import {debounceTime} from "rxjs/operators";
import {lazyInject} from "@athena-portal/core";

interface IPaymentScheduleProps {
    isOpen: boolean;
    instalments: Instalment[]; // instalments with possible gaps
    isPaidOff: boolean;
    interaction: any;
}

interface IPaymentScheduleState {
    instalmentsWithoutGaps: Instalment[]; // gaps filled with payment holidays
    lastDoneInstalmentIndex: number;
    isVulnerableCustomer: boolean;
    hasActivePaymentHoliday: boolean;
}

export default class PaymentSchedule extends React.Component<IPaymentScheduleProps, IPaymentScheduleState> {

    @lazyInject(Types.CustomerPortalService)
    private customerPortalService: ICustomerPortalService;

    private readonly progressContainerRef: React.RefObject<HTMLDivElement> = React.createRef();
    private readonly lastDoneInstalmentDotRef: React.RefObject<HTMLDivElement> = React.createRef();

    private windowResizeEventSubscription: Subscription;

    constructor(props: IPaymentScheduleProps) {
        super(props);

        this.state = {
            instalmentsWithoutGaps: [],
            lastDoneInstalmentIndex: -1,
            isVulnerableCustomer: false,
            hasActivePaymentHoliday: false
        };

        this.fillInstalmentGaps();
    }

    public componentDidMount() {
        this.customerPortalService.getPartyPropertyByPropertyKey(PartyPropertyEnum.VULNERABLE_CUSTOMER)
            .subscribe((vulnerabilityPartyProperty: PartyProperty) => {
                if (vulnerabilityPartyProperty !== null) {
                    this.setState({isVulnerableCustomer: vulnerabilityPartyProperty.value === "true"});

                    if (this.state.isVulnerableCustomer) {
                        this.customerPortalService.getPolicyPropertyByPropertyKey(this.props.interaction.agreementNumber!, PartyPropertyEnum.ACTIVE_VULNERABLE_CUSTOMER_ACTION)
                            .subscribe((activeAction: PartyProperty) => {
                                if (activeAction !== null) {
                                    if (activeAction.value === "PAYMENT_HOLIDAY") {
                                        this.setState({hasActivePaymentHoliday: true});
                                    }
                                }
                            });
                    }
                }
            });

        this.fillInstalmentGaps();
        this.updateActiveDotIndex();
        this.adjustProgressDom();

        this.windowResizeEventSubscription = fromEvent(window, "resize")
            .pipe(
                // Performance improvement by reducing adjustment frequency during resize
                debounceTime(300)
            )
            .subscribe(() => {
                this.adjustProgressDom();
            });
    }

    public componentDidUpdate(prevProps: IPaymentScheduleProps) {
        if(this.props.instalments !== prevProps.instalments) {
            this.setState({
                instalmentsWithoutGaps: []
            })
        }
        this.fillInstalmentGaps();
        this.updateActiveDotIndex();
        this.adjustProgressDom();
    }

    public componentWillUnmount() {
        if (this.windowResizeEventSubscription) {
            this.windowResizeEventSubscription.unsubscribe();
        }
    }

   public render() {
        return (
            <div className={`payment-schedule ${this.props.isOpen ? "payment-schedule--open" : ""}`}>
                <div
                    className="payment-schedule__progress-container"
                    ref={this.progressContainerRef}
                >
                    <div className="payment-schedule__progress">
                        <div className="payment-schedule__progress-bar" />
                    </div>
                </div>
                <div className="payment-schedule__dot-container">
                    {this.props.isOpen ? this.renderVerticalDotsWithSchedule() : this.renderHorizontalDots()}
                </div>
            </div>
        );
    }

    private renderHorizontalDots = (): React.ReactNode => {
        return this.state.instalmentsWithoutGaps.map((instalment, index) => {
            const isDone = index <= this.state.lastDoneInstalmentIndex;

            return (
                <div
                    key={index}
                    className={`progress-dot${isDone ? " progress-dot--paid" : ""}`}
                    ref={index === this.state.lastDoneInstalmentIndex ? this.lastDoneInstalmentDotRef : undefined}
                />
            );
        });
    }

    private renderVerticalDotsWithSchedule = (): React.ReactNode => {
        return this.state.instalmentsWithoutGaps.map((instalment, index, instalments) => {
            let paymentType = "";
            if (instalment.description?.includes("Deposit")) {
                paymentType = "deposit";
            } else if (instalment.description?.includes("PayOffBalance")) {
                paymentType = "pay off balance";
            } else if (!instalment.amount) {
                if (this.state.isVulnerableCustomer && this.state.hasActivePaymentHoliday) {
                    paymentType = "payment holiday";
                }
            }

            const isDone = index <= this.state.lastDoneInstalmentIndex || instalment.status === 'PAID';
            const highlightClassName = "font-weight-semi-bold text-blue-green";

            return (
                <React.Fragment key={index}>
                    <div className={`payment-schedule__grid-item ${isDone ? highlightClassName : ""}`}>
                        {moment(instalment.dueDate).format("DD MMM YYYY")}
                    </div>
                    <div className="payment-schedule__grid-item">
                        <div
                            className={`progress-dot${isDone ? " progress-dot--paid" : ""}`}
                            ref={index === this.state.lastDoneInstalmentIndex ? this.lastDoneInstalmentDotRef : undefined}
                        />
                    </div>
                    <div className={`payment-schedule__grid-item payment-plan-item ${paymentType ? "text-left deposit-amount-align": ""} ${isDone ? highlightClassName : ""}`}>
                        {`£${instalment.amount.toFixed(2)}${paymentType ? ` (${paymentType})` : ""}`}
                    </div>
                </React.Fragment>
            );
        });
    }
    /**
     * Finds gaps in instalments and fill them with mock payment holiday instalments
     */
    private fillInstalmentGaps = (): void => {
        const instalmentsWithoutGaps = [...this.props.instalments];

        if(this.state.hasActivePaymentHoliday) {
            for (let i = 0; i < instalmentsWithoutGaps.length; i++) {
                const instalment = instalmentsWithoutGaps[i];
                const prevInstalment = instalmentsWithoutGaps[i - 1];
                const dueDate = moment(instalment.dueDate);
                const startDate = moment(this.props.interaction.headerInfo.startDate);
                // if previous installment is not present or is deposit payment - use policy start date to calculate gap
                const dateToCompare = (prevInstalment && !prevInstalment.description.includes("Deposit")) ? moment(prevInstalment.dueDate) : startDate;
                const monthDiff = dueDate.clone().startOf("day").diff(dateToCompare.clone().date(dueDate.date()).startOf("day"), "months");

                // Search for gaps
                const gaps = monthDiff - 1;

                if (gaps > 0) {
                    // Create filler
                    const fillerInstalments: Instalment[] = [];
                    for (let j = gaps; j > 0; j--) {
                        fillerInstalments.push({
                            dueDate: dueDate.clone().subtract(j, "months").toISOString(),
                            amount: 0,
                        } as Instalment)
                    }

                    // Fill the gaps
                    instalmentsWithoutGaps.splice(i, 0, ...fillerInstalments);

                    i += gaps;
                }
            }
        }

        if (this.state.instalmentsWithoutGaps.length !== instalmentsWithoutGaps.length) {
            this.setState({
                instalmentsWithoutGaps
            });
        }
    }

    /**
     * Finds index of the dot at which the progress bar should end
     */
    private updateActiveDotIndex = (): void => {
        let lastDoneInstalmentIndex = -1;
        for (let i = 0; i < this.state.instalmentsWithoutGaps.length; i++) {
            const instalment = this.state.instalmentsWithoutGaps[i];
            const isPaid = !instalment.amount || instalment.status === "PAID";
            if (isPaid) {
                lastDoneInstalmentIndex = i;
            } else {
                break;
            }
        }
        if (this.state.lastDoneInstalmentIndex !== lastDoneInstalmentIndex) {
            this.setState({lastDoneInstalmentIndex});
        }
    }

    /**
     * Aligns progress bar to the dots
     */
    private adjustProgressDom = (): void => {
        const progressContainer = this.progressContainerRef.current;
        const dot = this.lastDoneInstalmentDotRef.current;

        if (progressContainer && dot) {
            const progress = progressContainer.children[0] as HTMLDivElement;
            const progressBar = progress.children[0]  as HTMLDivElement;

            // Reset style changes
            progressContainer.style.removeProperty("padding");
            progressBar.style.removeProperty("width");
            progressBar.style.removeProperty("height");

            if (this.props.isOpen) { // progress bar is positioned vertically
                // Align progress bar ends to first and last dots
                let dotRect = dot.getBoundingClientRect();
                const dotParentRect = dot.parentElement!.getBoundingClientRect();
                progressContainer.style.padding = `${(dotParentRect.height / 2) - (dotRect.height / 2) - 11}px 0`;

                // Set progress bar length
                dotRect = dot.getBoundingClientRect();
                const progressBarRect = progressBar.getBoundingClientRect();
                progressBar.style.height = `${dotRect.bottom - progressBarRect.top + 16}px`;
            } else { // progress bar is positioned horizontaly
                // Set progress bar length
                const dotRect = dot.getBoundingClientRect();
                const progressBarRect = progressBar.getBoundingClientRect();
                progressBar.style.width = `${dotRect.right - progressBarRect.left + 14}px`;
            }
        }
    }
}



// WEBPACK FOOTER //
// ./src/pages/dashboard/common/payments/paymentDetails/annual/PaymentSchedule.tsx